import { ReactNode, useState } from 'react';
import { Toaster } from 'sonner';
import {
  CheckmarkFilled,
  ErrorFilled,
  InformationFilled,
  WarningFilled,
} from '@carbon/icons-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@sharpe/ui/theme';
import { trpc, createTRPCClient } from '@/lib/trpc';
import { SidebarProvider } from '@/components/sidebar/SidebarContext';
import { SidebarInset, Sidebar } from '@/components/sidebar';

const TOAST_ICONS = {
  success: <CheckmarkFilled size={16} />,
  error: <ErrorFilled size={16} />,
  info: <InformationFilled size={16} />,
  warning: <WarningFilled size={16} />,
};

function SidebarAwareToaster() {
  return (
    <Toaster
      position="top-left"
      closeButton
      theme="system"
      icons={TOAST_ICONS}
      style={{ left: 'calc(var(--sidebar-w, 52px) + 18px)' }}
    />
  );
}

function TRPCProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            gcTime: 10 * 60 * 1000,
            retry: 1,
            refetchOnWindowFocus: true,
            refetchOnMount: true,
          },
        },
      })
  );

  const [trpcClient] = useState(() => createTRPCClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}

export function CoreProviders({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider defaultTheme="system" enableSystem>
      <TRPCProvider>{children}</TRPCProvider>
    </ThemeProvider>
  );
}

export function AuthProviders({ children }: { children: ReactNode }) {
  return (
    <SidebarProvider defaultOpen={false}>
      <SidebarAwareToaster />
      <Sidebar />
      <SidebarInset>{children}</SidebarInset>
    </SidebarProvider>
  );
}
